import { Directive, ElementRef, HostListener, Input, OnInit, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
    selector: '[appAutoResizeInput]',
    standalone: true
})
export class AutoResizeInputDirective implements OnInit, OnDestroy {
    @Input() minWidth: number = 100;
    @Input() maxWidth: number = 600;
    @Input() bufferSpace: number = 20;

    private mirrorSpan: HTMLSpanElement | null = null;

    constructor(
        private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
        private renderer: Renderer2
    ) { }

    ngOnInit(): void {
        this.createMirrorElement();
        this.updateWidth();
    }

    ngOnDestroy(): void {
        if (this.mirrorSpan && this.mirrorSpan.parentNode) {
            this.mirrorSpan.parentNode.removeChild(this.mirrorSpan);
        }
    }

    @HostListener('input')
    @HostListener('change')
    onInput(): void {
        this.updateWidth();
    }

    private createMirrorElement(): void {
        // Create a hidden span element to measure text width
        this.mirrorSpan = this.renderer.createElement('span');

        // Copy computed styles from the input/textarea
        const computedStyle = window.getComputedStyle(this.el.nativeElement);
        this.renderer.setStyle(this.mirrorSpan, 'position', 'absolute');
        this.renderer.setStyle(this.mirrorSpan, 'visibility', 'hidden');
        this.renderer.setStyle(this.mirrorSpan, 'white-space', 'pre');
        this.renderer.setStyle(this.mirrorSpan, 'font-family', computedStyle.fontFamily);
        this.renderer.setStyle(this.mirrorSpan, 'font-size', computedStyle.fontSize);
        this.renderer.setStyle(this.mirrorSpan, 'font-weight', computedStyle.fontWeight);
        this.renderer.setStyle(this.mirrorSpan, 'font-style', computedStyle.fontStyle);
        this.renderer.setStyle(this.mirrorSpan, 'letter-spacing', computedStyle.letterSpacing);
        this.renderer.setStyle(this.mirrorSpan, 'padding-left', computedStyle.paddingLeft);
        this.renderer.setStyle(this.mirrorSpan, 'padding-right', computedStyle.paddingRight);
        this.renderer.setStyle(this.mirrorSpan, 'border-left-width', computedStyle.borderLeftWidth);
        this.renderer.setStyle(this.mirrorSpan, 'border-right-width', computedStyle.borderRightWidth);
        this.renderer.setStyle(this.mirrorSpan, 'box-sizing', computedStyle.boxSizing);

        // Append to body
        this.renderer.appendChild(document.body, this.mirrorSpan);
    }

    private updateWidth(): void {
        if (!this.mirrorSpan) return;

        const element = this.el.nativeElement;
        const value = element.value || element.placeholder || '';

        // Update mirror span content
        this.mirrorSpan.textContent = value || ' ';

        // Get the width of the mirror span
        const textWidth = this.mirrorSpan.offsetWidth;

        // Calculate new width with buffer space
        let newWidth = textWidth + this.bufferSpace;

        // Apply min/max constraints
        newWidth = Math.max(this.minWidth, Math.min(newWidth, this.maxWidth));

        // Apply the width to the input/textarea
        this.renderer.setStyle(element, 'width', `${newWidth}px`);
    }
}
